import { useSession } from "@/services/auth0/session";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Phase 10b: customer-facing list of attachable upcoming pickup bookings.
 *
 * Admin reuses the pickup-bookings attachable view. Used in
 * the admin's create-order flow so when admin places an order on behalf of a
 * customer who already has an upcoming pickup at the same saved sender
 * address, we surface the option to attach.
 */

export interface AttachablePickupBooking {
  slotId?: string;
  timingAvailability?: "CAPTURED" | "UNKNOWN_LEGACY";
  timeZone?: string | null;
  schedulingTimeZone?: string | null;
  id: string;
  pickupAddressId: string;
  pickupAddress: {
    id: string;
    line1: string;
    line2?: string | null;
    city: string;
    province: string;
    country: string;
    postalCode?: string | null;
  };
  scheduledDate: string;
  slot: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  } | null;
  dispatchCenter: { id: string; code: string; name: string };
  pickupService: { id: string; code: string; name: string };
  attachedShipmentCount: number;
  notes: string | null;
}

interface AttachableBookingsResponse {
  data: AttachablePickupBooking[];
}

interface UseAttachablePickupBookingsArgs {
  /** Customer the order is being placed for. */
  customerId?: string;
  /** AddressBook entry ID picked as the sender. */
  addressBookEntryId?: string;
}

export function useAttachablePickupBookings({
  customerId,
  addressBookEntryId,
}: UseAttachablePickupBookingsArgs) {
  const { token } = useSession();

  return useQuery<AttachableBookingsResponse>({
    queryKey: ["attachablePickupBookings", customerId, addressBookEntryId],
    queryFn: async () => {
      const response = await axios.get<AttachableBookingsResponse>(
        `${API_URL}/api/v2/pickup-bookings`,
        {
          params: { view: "attachable", customerId, addressBookEntryId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!token && !!customerId && !!addressBookEntryId,
    staleTime: 30_000,
  });
}
